import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
} from '@bybeam/platform-types';
import { formatIfPhone } from '@platform-ui-common/utilities/format.js';
import {
  AgreementOptions,
  ConfidentOptions,
  LikelyOptions,
  SatisfiedOptions,
} from '@platform-ui/application/utils/index.js';
import { eq, or } from '../../ast/utils.js';
import {
  COMMON_OVERRIDES,
  COMMON_PAGES,
  COMMON_QUESTIONS,
  COMMON_SECTIONS,
  VERIFICATION_FORM_LINK,
} from './common.js';

const ELIGIBLE_ROLES = [
  'Community Health Worker (in a home setting ONLY)',
  'Home Health Aide (HHN)',
  'Certified Nursing Assistant (CNA)',
  'Registered Nurse (RN)',
  'Licensed Vocational Nurse (LVN)',
  'Respiratory Therapist',
  'Occupational Therapist',
  'Speech Therapist',
  'Physical Therapist',
  'Licensed Clinical Social Worker',
  'Restorative Nursing Assistant',
];

const QUESTIONS: {
  [key in
    | 'documents'
    | 'employers'
    | 'eligibleRoles'
    | 'surveyLevel1'
    | 'surveyLevel2Group1'
    | 'surveyLevel2Group2'
    | 'surveyLevel2Group3'
    | 'surveyLevel3Group1'
    | 'surveyLevel3Group2'
    | 'surveyLevel3Group3'
    | 'numberOfCertificationTests']: ApplicationQuestion;
} = {
  documents: {
    key: 'documents',
    copy: {
      title: 'You have 5 document upload options to claim this incentive level',
      intro: 'Please select one option below and upload the required documentation.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'documentType',
        options: [
          {
            label:
              'Proof of employment or supervised volunteering in an eligible job role and setting in California for at least 40 hours for one month since the beginning of the Caring4Cal program.',
            description: `
Please have your employer(s) complete this [employment verification form](${VERIFICATION_FORM_LINK}) and then upload below. If you have more than one employer, have each employer complete the employment verification form.`,
            value: 'employment',
          },
          {
            label: 'Proof of registration for an eligible license/certification exam.',
            description:
              'Go [here](https://www.caring4cal.org/en/faq/#eligibility) to view Caring4Cal Incentives FAQs, including what documents qualify as proof.',
            value: 'licenseRegistration',
          },
          {
            label: 'Proof of an eligible license/certification earned since November 1, 2023.',
            description:
              'Note: course completion certificates do not qualify as proof.  Go [here](https://www.caring4cal.org/en/faq/#eligibility) to view Caring4Cal Incentives FAQs, including what documents qualify as proof.',
            value: 'certification',
          },
          {
            label:
              'Proof of registration for, or application to, coursework toward a license or certification in an eligible job role.',
            description:
              'Go [here](https://www.caring4cal.org/en/faq/#eligibility) to view Caring4Cal Incentives FAQs, including what documents qualify as proof.',
            value: 'courseworkRegistration',
          },
          {
            label: 'Proof of current employment or supervised volunteering as a home-based CHW.',
            description: `Please have your employer(s) complete this [employment / volunteering verification form](${VERIFICATION_FORM_LINK}) to show proof of current employment or supervised volunteering as a home-based CHW.`,
            value: 'employmentHomeBasedCHW',
          },
        ],
      },
      {
        copy: { title: 'Documents' },
        type: FieldType.Document,
        key: 'documents.proofOfLevel3',
      },
    ],
  },
  employers: mergeObjects(
    [
      COMMON_QUESTIONS.employers,
      {
        dynamicLogic: eq('documentType', 'employment'),
      },
    ],
    'merge',
  ) as ApplicationQuestion,
  eligibleRoles: {
    key: 'eligibleRoles',
    copy: {
      title:
        'I accepted employment or a supervised volunteer position in the following eligible job role(s):',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Eligible roles',
        key: 'eligibleRole.eligibleRole',
        options: ELIGIBLE_ROLES.map((value: string) => ({ label: value, value })),
        props: { multiple: true },
      },
    ],
    dynamicLogic: eq('documentType', 'employment'),
  },
  numberOfCertificationTests: {
    key: 'numberOfCertificationTests',
    copy: {
      title:
        'Congratulations on achieving this goal! We know those tests take time and dedication. Would you please tell us how many times total you took the test before passing?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        copy: 'Number of Tests',
        key: 'numberOfCertificationTests',
        options: [
          { label: '1', value: '1-time' },
          { label: '2', value: '2-times' },
          { label: '3', value: '3-times' },
          { label: '4', value: '4-times' },
          { label: '5', value: '5-times' },
          { label: 'More than 5', value: 'more-than-5-times' },
        ],
      },
    ],
    dynamicLogic: eq('documentType', 'certification'),
  },
  surveyLevel1: {
    key: 'surveyLevel1',
    copy: {
      title: 'Please rate the following statements.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.was-able-to-find-and-take-training-courses-fit-my-interests',
        copy: 'I was able to find and take training courses that fit my interests.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.satisfied-with-training-you-participated-in',
        copy: 'How satisfied are you with the training you participated in?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.without-this-program-not-have-taken-trainings',
        copy: 'Without this program, I would not have taken trainings like these at this time.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.after-taking-trainings-confident-about-future-career',
        copy: 'After taking these trainings, I feel more confident about my future career as a care provider.',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.satisfied-with-the-coaching-services',
        copy: 'If you received texts from, or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
  },
  surveyLevel2Group1: {
    key: 'surveyLevel2Group1',
    copy: {
      title: 'Please rate the following statements.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.was-able-to-find-and-take-training-courses-fit-my-interests',
        copy: 'I was able to find and take training courses that fit my interests.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.satisfied-with-training-you-participated-in',
        copy: 'How satisfied are you with the training you participated in?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.without-this-program-not-have-taken-trainings',
        copy: 'Without this program, I would not have taken trainings like these at this time.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.satisfied-with-the-coaching-services',
        copy: 'If you received texts from, or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
  },
  surveyLevel2Group2: {
    key: 'surveyLevel2Group2',
    copy: {
      title: 'Please rate the following statements.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.courses-were-relevant-to-skills',
        copy: 'The courses I took were relevant to the skills I need in my career as a care provider.',
        options: [...AgreementOptions, { value: 'unsure', label: 'Unsure' }],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.after-taking-trainings-confident-about-future-career',
        copy: 'After taking these trainings, I feel more confident about my future career as a care provider.',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.learned-new-skills-that-are-important-to-my-work-as-care-provider',
        copy: 'I learned new skills that are important to my work as a future care provider.',
        options: [...AgreementOptions, { value: 'unsure', label: 'Unsure' }],
        validation: { required: false },
      },
    ],
  },
  surveyLevel2Group3: {
    key: 'surveyLevel2Group3',
    copy: {
      title: 'Please rate the following statements.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.courses-will-advance-my-career',
        copy: 'The courses I took will help me advance in my career.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.communication-with-coach-will-advance-my-career',
        copy: 'If you received texts from, or had a call with a coach, how much do you agree with the following statement, "The support my coach gave me will help me advance in my career."?',
        options: [
          ...AgreementOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
  },
  surveyLevel3Group1: {
    key: 'surveyLevel3Group1',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.courses-have-helped-me-advance-my-career',
        copy: 'The courses I took have helped me advance in my career.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.learned-new-skills-that-are-important-to-my-work-as-care-provider',
        copy: 'I learned new skills that will be important to my work as a care provider.',
        options: [...AgreementOptions, { value: 'unsure', label: 'Unsure' }],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.feel-confident-to-provide-high-quality-care',
        copy: 'How confident do you feel in your ability to provide high quality care to your patients and clients in your new role?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.better-provide-care-to-patients-from-diverse-backgrounds',
        copy: 'I can better provide care to patients and clients from diverse backgrounds.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
  },
  surveyLevel3Group2: {
    key: 'surveyLevel3Group2',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.likely-to-work-as-care-provider-next-12-months',
        copy: 'How likely are you to work as a care provider for the next 12 months (or the first 12 months in a new role if not yet working as a care provider)?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.likely-to-work-in-home-or-community-based-settings-next-12-months',
        copy: 'How likely are you to work in home- or community-based settings for the next 12 months (or the first 12 months in a new role if not yet working as a care provider)?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.satisfied-with-career-as-care-provider',
        copy: 'If you have started a new role as a care provider in a home- or community-based setting, how satisfied do you feel with your career today?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'have-not-started-a-new-role',
            label: 'I have not started a new role',
          },
        ],
        validation: { required: false },
      },
    ],
  },
  surveyLevel3Group3: {
    key: 'surveyLevel3Group3',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.satisfied-with-the-coaching-services',
        copy: 'If you received texts from, or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.communication-with-coach-will-advance-my-career',
        copy: 'If you received texts from, or had a call with a coach, how much do you agree with the following statement, "The support my coach gave me will help me advance in my career."?',
        options: [
          ...AgreementOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
  },
};

const SECTIONS: ApplicationSection[] = [
  {
    key: 'incentiveLevelSelection',
    name: 'Incentive Level Selection',
    overview: {
      description: `
You will complete a full application for each incentive payment request. Once you complete each application, you will be eligible to complete and submit another incentive payment request.
  
**Participants may claim each incentive level once in EITHER Track 1 OR Track 2. Do not submit multiple applications for the same level in the same or different tracks.**`,
      title: 'Incentive Level Selection',
    },
    questionGroups: [
      {
        key: 'firstTimeApplying',
        name: 'First Time Applying',
        questions: [COMMON_QUESTIONS.firstTimeApplying],
      },
      {
        key: 'incentiveLevelsGroup',
        name: 'Incentive Level Selection',
        dynamicLogic: eq('firstTimeApplying', 'no'),
        questions: [COMMON_QUESTIONS.incentiveLevels],
      },
      {
        key: 'incentiveLevel1',
        name: "You selected 'Level 1: Jump Start'",
        overview: {
          title: "You selected 'Level 1: Jump Start'",
          description: `
You need at least 15 hours of Caring4Cal training to claim this incentive.

**You may claim Level 1 only once.**`,
        },
        dynamicLogic: or([eq('incentiveLevel', 'level1'), eq('firstTimeApplying', 'yes')]),
        questions: [
          COMMON_QUESTIONS.courseCompletedL1,
          {
            key: 'surveyQuestionLevel1',
            copy: {
              title:
                'We’d like to ask you a few questions about your experience so far with Caring4Cal.',
            },
            fields: [],
          },
          QUESTIONS.surveyLevel1,
        ],
      },
      {
        key: 'incentiveLevel2',
        name: "You selected 'Level 2: Leap Forward'",
        overview: {
          title: "You selected 'Level 2: Leap Forward'",
          description: `
You need at least 15 hours of additional Caring4Cal Training to claim Level 2 and must have claimed Level 1.

**You may claim Level 2 only once.**`,
        },
        dynamicLogic: eq('incentiveLevel', 'level2'),
        questions: [
          COMMON_QUESTIONS.courseCompletedL2,
          {
            key: 'surveyQuestionLevel2',
            copy: {
              title:
                'We’d like to ask you a few questions about your experience so far with Caring4Cal.',
            },
            fields: [],
          },
          QUESTIONS.surveyLevel2Group1,
          QUESTIONS.surveyLevel2Group2,
          QUESTIONS.surveyLevel2Group3,
        ],
      },
      {
        key: 'incentiveLevel3',
        name: "You selected 'Level 3: Retention & Recruitment'",
        overview: {
          title: "You selected 'Level 3: Retention & Recruitment'",
          description: `
You must have claimed Levels 1 and 2 to be eligible to claim this level. 

**You may claim Level 3 only once.**`,
        },
        dynamicLogic: eq('incentiveLevel', 'level3'),
        questions: [
          QUESTIONS.documents,
          QUESTIONS.numberOfCertificationTests,
          QUESTIONS.eligibleRoles,
          QUESTIONS.employers,
          {
            key: 'surveyQuestionLevel3',
            copy: {
              title: 'We’d like to ask you a few questions about your experience with Caring4Cal.',
            },
            fields: [],
          },
          QUESTIONS.surveyLevel3Group1,
          QUESTIONS.surveyLevel3Group2,
          QUESTIONS.surveyLevel3Group3,
        ],
      },
      {
        key: CommonQuestionKeys.ADDRESS,
        name: 'Home Address',
        questions: [COMMON_QUESTIONS[CommonQuestionKeys.ADDRESS]],
      },
      {
        key: CommonQuestionKeys.MAILING_ADDRESS,
        name: 'Mailing Address',
        questions: [COMMON_QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS]],
      },
    ],
  },
  COMMON_SECTIONS.paymentInfoSection,
  COMMON_SECTIONS.review,
];
const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'Welcome to Caring4Cal!',
      body: `
Please read the following carefully. 
  
**You chose this track because you do NOT have a current California-issued license or certification in an eligible job role and want to get one, or are not a CHW and want to become one.**
    
Not sure if you have an eligible job role? Review eligibility guidelines [here](https://www.caring4cal.org/en/eligibility/) or call (844) 425-1516.

If you DO meet the eligibility requirements above for Track 1, click CONTINUE. **If you DO NOT, please go back and select Track 2.**`,
    },
    COMMON_PAGES.applicationProcess,
    COMMON_PAGES.termsOfServiceAndPrivacyPolicy,
    {
      key: 'attestation',
      layout: 'default',
      title: 'Attestation',
      body: 'By tapping CONTINUE, I acknowledge that I intend to work toward an eligible job role and plan to seek employment or a supervised volunteering position in an eligible job setting. If I become an Community Health Worker (CHW), I acknowledge that I will work or volunteer under supervision in a home-based (not community-based) setting to be eligible for a Level 3 incentive.',
    },
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        message: `
**Do Not Have A Current License or Certification or a role as a CHW**

Please be sure to complete your Caring4Cal Registration before claiming incentives.

**Are you in the right track?**

**Choose this track if you do NOT have a current California-issued license or certification in an eligible job role and want to get one, or are not a CHW and want to become one. Please review eligibility guidelines [here](https://www.caring4cal.org/en/eligibility) or call ${formatIfPhone(
          '+18444251516',
        )} to confirm.**
        
You may claim each incentive level only once, regardless of which track you choose.`,
      },
      {
        status: 'Incomplete',
        message:
          'You’ve made great progress so far! Keep working on your Caring4Cal incentive application.',
      },
      {
        status: 'InProgress',
        message:
          'You’ve made great progress so far! Keep working on your Caring4Cal incentive application.',
      },
      {
        status: 'AwaitingReview',
        message:
          'Thanks for submitting your application! Your application will be reviewed in the order it was received. We will contact you if we have any questions. Check your email or back here for updates.',
      },
      {
        status: 'InReview',
        message:
          'We’re currently reviewing your application and will reach a decision shortly. You’ll receive an email once a decision has been made and no further action is required on your part.',
      },
      {
        status: 'Denied',
        message:
          'Your application was denied. Please check the email that was sent to you for more information.',
      },
    ],
    submission: COMMON_OVERRIDES.submission,
  },
  sections: SECTIONS,
};
export default CONFIG;
